import { useState } from "react";
import Reveal from "@/components/Reveal";
import IndiaMap from "@/components/IndiaMap";
import { MAP_POINTS } from "@/data/mapPoints";

const LAYERS = [
  { id: "all", label: "All Signals", color: "#F8FAFC" },
  { id: "ocean", label: "Ocean", color: "#00E5FF" },
  { id: "agriculture", label: "Agriculture", color: "#10B981" },
];

export default function MapShowcase() {
  const [layer, setLayer] = useState("all");
  const [selected, setSelected] = useState(null);

  const visibleCount = MAP_POINTS.filter((p) => layer === "all" || p.type === layer).length;

  return (
    <section id="livemap" className="relative overflow-hidden bg-arjuna-ink py-24 sm:py-32" data-testid="map-showcase">
      <div className="absolute inset-0 grid-lines opacity-30" />
      <div className="container-x relative">
        <div className="max-w-2xl">
          <Reveal>
            <p className="overline">04. Live Map</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl">
              India, in real time.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-base leading-relaxed text-slate-400">
              A live snapshot of active monitoring nodes. Toggle layers to isolate maritime
              or agricultural signals, and hover any node for its latest reading.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* controls + detail */}
          <div className="lg:col-span-4">
            <Reveal>
              <div className="glass rounded-2xl p-6">
                <p className="overline mb-4">Data layers</p>
                <div className="flex flex-col gap-2">
                  {LAYERS.map((l) => (
                    <button
                      key={l.id}
                      data-testid={`layer-toggle-${l.id}`}
                      onClick={() => setLayer(l.id)}
                      className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm transition-colors duration-200 ${
                        layer === l.id
                          ? "border-arjuna-teal/50 bg-white/5 text-white"
                          : "border-white/10 text-slate-400 hover:border-white/25"
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: l.color }} />
                        {l.label}
                      </span>
                      {layer === l.id && <span className="font-mono text-xs text-arjuna-teal">ON</span>}
                    </button>
                  ))}
                </div>

                <div className="mt-6 border-t border-white/10 pt-6">
                  <p className="font-mono text-xs text-slate-500">
                    ACTIVE NODES <span className="text-arjuna-teal">{visibleCount}</span>
                  </p>
                  {selected ? (
                    <div className="mt-4" data-testid="map-selected-detail">
                      <p className="font-heading text-lg text-white">{selected.name}</p>
                      <p className="font-mono text-[11px] uppercase tracking-wider text-slate-400">
                        {selected.region}
                      </p>
                      <div className="mt-4 flex items-center justify-between rounded-lg bg-white/5 px-4 py-3">
                        <span className="text-sm text-slate-400">{selected.metric}</span>
                        <span
                          className="font-mono text-sm font-semibold"
                          style={{ color: selected.type === "ocean" ? "#00E5FF" : "#10B981" }}
                        >
                          {selected.value}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-4 text-sm text-slate-500">
                      Select a node on the map to inspect its latest telemetry.
                    </p>
                  )}
                </div>
              </div>
            </Reveal>
          </div>

          {/* map */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="glass mx-auto max-w-md rounded-2xl p-4 sm:p-6">
                <IndiaMap points={MAP_POINTS} activeLayer={layer} onSelect={setSelected} />
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
